import { useCallback, useEffect, useState, useRef } from 'react';
import {
  EVENTS_EMIT,
  useSocketConversationChatTs
} from './useSocketConversationChatTs';
import { toast } from 'react-toastify';
import { useDebounce } from './useDebouce';
import { useQueueChatPauseContext } from './useQueuePauseChatServicePanel';
import { useSocketEvents, useSocketRoom } from './socket-utils';

/**
 * @typedef {'queue' | 'agent' | 'agent_queue'} GroupOptions
 */

/**
 * @typedef {'fullyOccupied' | 'partiallyOccupied' | 'totallyFree'} OccupationStatus
 */

/**
 * @typedef {Object} QueueType
 * @property {string} name
 * @property {number} id
 * @property {number} finishedChats
 * @property {number} chatsInAttendance
 * @property {number} chatsAssigned
 * @property {'isPaused' | 'isActivePause' | 'isAvailable'} status
 * @property {number | null} pauseId
 * @property {Date | null} dateOfPause
 * @property {Date | null} dateOfLogin
 * @property {OccupationStatus} [occupationStatus]
 */

/**
 * @typedef {Object} DataGroupedByAgent
 * @property {string} agentLogin
 * @property {string} agentName
 * @property {number} finishedChats
 * @property {number} chatsInAttendance
 * @property {number} chatsAssigned
 * @property {'isPaused' | 'isActivePause' | 'isAvailable'} status
 * @property {number | null} pauseId
 * @property {Date | null} dateOfPause
 * @property {Date} dateOfLogin
 * @property {{name: string, id: number}[]} queues
 * @property {OccupationStatus} [occupationStatus]
 */

/**
 * @typedef {Object} DataGroupedByQueue
 * @property {string} agentLogin
 * @property {string} agentName
 * @property {QueueType[]} queues
 */

/**
 * @typedef {Object} AgentType
 * @property {string} login
 * @property {string} name
 * @property {'isPaused' | 'isActivePause' | 'isAvailable'} status
 * @property {number | null} pauseId
 */

/**
 * @typedef {Object} QueueMetricsType
 * @property {string} name
 * @property {number} id
 * @property {number} chatsInAttendance
 * @property {number} chatInWaiting
 * @property {string} TMA
 * @property {string} TME
 * @property {AgentType[]} agents
 */

/**
 * @typedef {Object} DataToUnifiedQueueMonitoringOutput
 * @property {QueueMetricsType | DataGroupedByAgent | DataGroupedByQueue} data
 * @property {GroupOptions} group
 */

/**
 * @typedef {Object} ResponseType
 * @property {DataGroupedByAgent[] | QueueMetricsType[]} response Array de respostas que varia de acordo com o parâmetro group
 * @property {() => void} emitEventInJoinMonitoringQueueChat Função para entrar no monitoramento
 * @property {() => void} emitEventLeaveMonitoringQueueChat Função para sair do monitoramento
 */

/**
 * @typedef {Object} GroupedResponse
 * @property {DataGroupedByAgent[]} agent
 * @property {DataGroupedByAgent[]} agent_queue
 * @property {QueueMetricsType[]} queue
 */

/**
 * @typedef {Object} StateType
 * @property {GroupedResponse} response
 */

/**
 * Expande os dados agrupados por fila, criando um objeto para cada fila com os dados do agente
 * @param {DataGroupedByQueue} queueData - Dados agrupados por fila
 * @returns {DataGroupedByAgent[]} - Array de objetos expandidos, um para cada fila
 */
const expandQueueDataToAgentFormat = (queueData) => {
  // Para cada fila do agente, cria um novo objeto combinando dados do agente com dados da fila
  return queueData.queues.map((queue) => ({
    agentLogin: queueData.agentLogin,
    agentName: queueData.agentName,
    queueId: queue.id,
    queueName: queue.name,
    finishedChats: queue.finishedChats,
    chatsInAttendance: queue.chatsInAttendance,
    occupationStatus: queue.occupationStatus,
    chatsAssigned: queue.chatsAssigned,
    status: queue.status || 'isAvailable',
    pauseId: queue.pauseId || null,
    dateOfPause: queue.dateOfPause || null,
    dateOfLogin: queue.dateOfLogin || null,
    queues: [{ name: queue.name, id: queue.id }]
  }));
};

/**
 * Estabelece uma conexão via websocket com o Chat TS para compartilhar as informações de monitoramento de filas de chat
 * @param {Object} props
 * @param {string[]} [props.queueNames=[]] Lista de filas para monitorar
 * @param {GroupOptions} [props.group="agent"] Tipo de agrupamento desejado:
 * - 'queue': Agrupa por fila, retornando métricas da fila e lista de agentes
 * - 'agent' ou 'agent_queue': Agrupa por agente, retornando dados detalhados de cada agente
 * @param {(res: {successfull: string[], rejected: string[]}) => void} [props.onJoined] Um callback opcional para caso o desenvolvedor precise
 * @returns {ResponseType} Retorna diferentes tipos de resposta baseado no parâmetro group:
 * - Quando group é 'queue': Retorna { response: QueueMetricsType[] }
 * - Quando group é 'agent' ou 'agent_queue': Retorna { response: DataGroupedByAgent[] }
 */
export const useMonitoringChatQueuesSocket = (
  { queueNames = [], channels = [], group = 'agent', agents = [], onJoined } = {
    queueNames: [],
    channels: [],
    group: 'agent',
    agents: []
  }
) => {
  const { socketChatTS } = useSocketConversationChatTs();
  const { getInitialValuesQueueChatPause } = useQueueChatPauseContext();
  const { withDebounce } = useDebounce();
  const [state, setState] = useState({
    response: {
      agent: [],
      agent_queue: [],
      queue: []
    },
    isLoading: true
  });

  /**
   * O socket tende a retornar dezenas de eventos UPDATE dentro de um curto espaço de tempo, o que causaria
   * um layout-shift muito grande, se processados de maneira serializada; por isso, optei por utilizar um batch desses UPDATES,
   * por meio do hook `useDebouce`.
   * A referência abaixo, então, guarda os UPDATES que chegam em um espaço de tempo curto para que sejam processados todos de uma vez.
   */
  const pendingUpdates = useRef([]);

  /**
   * Determina se é a primeira vez que a tela está sendo renderizada;
   * Utilizado para mostrar determinar o `isLoading`, evitando estados de carregamento indevidos, como alguns UPDATE;
   * Reseta sempre que o agrupamento mudar
   */
  const isRenderRef = useRef(false);

  useEffect(() => {
    isRenderRef.current = true;
    setState((prev) => ({
      ...prev,
      response: {
        ...prev.response,
        [group]: []
      },
      isLoading: true
    }));
  }, [
    group,
    JSON.stringify(agents),
    JSON.stringify(queueNames),
    JSON.stringify(channels)
  ]);

  const processBatchedUpdates = useCallback(
    withDebounce(() => {
      setState((prev) => {
        const newResponse = { ...prev.response };

        pendingUpdates.current.forEach((update) => {
          if (update.group === 'queue') {
            const filteredResponse = newResponse.queue.filter(
              (queue) => queue.name !== update.data.name
            );
            const queueData = {
              name: update.data.name,
              id: update.data.id,
              chatsInAttendance: update.data.chatsInAttendance,
              chatInWaiting: update.data.chatInWaiting,
              TMA: update.data.TMA,
              TME: update.data.TME,
              agents: update.data.agents || []
            };
            newResponse.queue = [...filteredResponse, queueData];
          } else {
            const groupKey = update.group;
            const filteredResponse = newResponse[groupKey].filter(
              (old) => old.agentLogin !== update.data.agentLogin
            );
            const newData =
              update.group === 'agent_queue'
                ? expandQueueDataToAgentFormat(update.data)
                : [update.data];
            newResponse[groupKey] = [...filteredResponse, ...newData];
          }
        });

        pendingUpdates.current = [];
        isRenderRef.current = false;

        return {
          ...prev,
          response: newResponse,
          isLoading: false
        };
      });
    }, 300),
    []
  );

  const roomKey = JSON.stringify({
    queueNames,
    group,
    channels: channels?.sort?.() || []
  });

  useSocketRoom(
    socketChatTS,
    roomKey,
    EVENTS_EMIT.JOIN_IN_MONITORING_QUEUE_CHAT,
    EVENTS_EMIT.LEAVE_MONITORING_QUEUE_CHAT,
    {
      queueNames,
      group,
      channels,
      agentLogins: agents?.map?.((a) => a.login),
      queueNames
    }
  );

  useSocketEvents(socketChatTS, {
    [EVENTS_EMIT.JOINED_IN_MONITORING_QUEUE_CHAT]: (res) => {
      typeof onJoined === 'function' &&
        onJoined(res?.queues ?? { successfull: [], rejected: [] });

      setState((prev) => ({ ...prev, isLoading: false }));

      if (res?.queues?.rejected?.length) {
        toast.error(
          `Não foi possível monitorar as seguintes filas: ${res.queues.rejected.join(
            ', '
          )}`,
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
      }
    },
    [EVENTS_EMIT.ERROR_MONITORING_QUEUE_CHAT]: (error) => {
      if (error?.ERROR === 'EMPTY_SUBSCRIBERS') {
        setState((prev) => ({ ...prev, isLoading: true }));
      }

      window.location.reload();
    },
    [EVENTS_EMIT.TOGGLE_CHAT_AGENT_PAUSE]: withDebounce((update) => {
      if (update.agentLogin === localStorage.getItem('login')) {
        getInitialValuesQueueChatPause();
      }

      setState((prev) => {
        const status = update.isPaused ? 'isPaused' : 'isAvailable';
        const newState = { ...prev };
        const newResponse = { ...prev.response };

        if (group === 'agent') {
          newResponse.agent = prev.response.agent.map((agent) =>
            agent.agentLogin === update.agentLogin
              ? {
                  ...agent,
                  status,
                  pauseId: update.pauseId,
                  dateOfPause: update.isPaused ? new Date() : null
                }
              : agent
          );
        } else if (group === 'agent_queue') {
          newResponse.agent_queue = prev.response.agent_queue.map((agent) =>
            agent.agentLogin === update.agentLogin
              ? {
                  ...agent,
                  status,
                  pauseId: update.pauseId,
                  dateOfPause: update.isPaused ? new Date() : null
                }
              : agent
          );
        } else if (group === 'queue') {
          newResponse.queue = prev.response.queue.map((queue) => ({
            ...queue,
            agents: queue.agents.map((agent) =>
              agent.login === update.agentLogin
                ? {
                    ...agent,
                    status,
                    pauseId: update.pauseId
                  }
                : agent
            )
          }));
        }

        newState.response = newResponse;
        return newState;
      });
    }, 300),
    [EVENTS_EMIT.UPDATE_MONITORING_QUEUE_CHAT_DATA]: (update) => {
      pendingUpdates.current.push(update);

      if (isRenderRef.current)
        setState((prev) => ({ ...prev, isLoading: true }));

      processBatchedUpdates();
    }
  });

  return {
    ...state,
    emitEventInJoinMonitoringQueueChat: () => {}, // No-op for compatibility
    emitEventLeaveMonitoringQueueChat: () => {} // No-op for compatibility
  };
};
